import { Injectable, NgZone } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { Store } from '@ngxs/store';
import { TransferTargetModel } from 'gp-asterisk';
import { BehaviorSubject, filter, firstValueFrom, timeout } from 'rxjs';
import { GpAuthState } from 'gp-auth';
import { InternalCallService } from './internal-call.service';
import { SoftphoneService } from './softphone.service';

export interface QuickDialContact {
  sip: string;
  name: string;
}

// Нативні віджети «Швидкий набір» (iOS: QuickDialWidget + QuickDialPlugin.swift, Android: QuickDialWidgetProvider)
interface QuickDialPlugin {
  setFavorites(options: { contacts: QuickDialContact[] }): Promise<void>;
}

const QuickDial = registerPlugin<QuickDialPlugin>('QuickDial');
const FAVORITES_KEY = 'quick_dial_favorites';
// посилання з віджета: vaillant-service://call/<номер>
const CALL_URL = /^vaillant-service:\/\/call\/(\d+)/;

/**
 * Обрані колеги для швидкого набору: зірочка у списку «Співробітники» -> віджет на екрані «Додому».
 * Дотик у віджеті відкриває застосунок посиланням, і ми одразу дзвонимо через вбудований телефон.
 */
// посилання запуску (віджет), уже оброблене в цьому запуску застосунку — sessionStorage переживає перезавантаження сторінки
const LAUNCH_URL_HANDLED_KEY = 'quick_dial_launch_url_handled';

@Injectable({ providedIn: 'root' })
export class QuickDialService {
  readonly favorites$ = new BehaviorSubject<QuickDialContact[]>(QuickDialService.read());
  private lastUrl: { url: string; at: number } | null = null;

  constructor(
    private internalCalls: InternalCallService,
    private softphone: SoftphoneService,
    private store: Store,
    private zone: NgZone
  ) {}

  init(): void {
    if (!Capacitor.isNativePlatform()) return;
    this.syncWidget();
    App.addListener('appUrlOpen', ({ url }) => this.zone.run(() => this.handleUrl(url))).catch(() => {});
    // застосунок запустили дотиком у віджеті
    // посилання запуску лишається тим самим, доки застосунок не закриють: після оновлення веб-частини (OTA) сторінка
    // перезавантажується і читала його знову — дзвінок повторювався. Оброблене запам'ятовуємо до закриття застосунку
    App.getLaunchUrl().then(launch => {
      if (!launch?.url || sessionStorage.getItem(LAUNCH_URL_HANDLED_KEY) === launch.url) return;
      sessionStorage.setItem(LAUNCH_URL_HANDLED_KEY, launch.url);
      this.zone.run(() => this.handleUrl(launch.url));
    }).catch(() => {});
  }

  isFavorite(sip: string): boolean {
    return this.favorites$.value.some(item => item.sip === sip);
  }

  toggle(contact: TransferTargetModel): void {
    const favorites = this.isFavorite(contact.username)
      ? this.favorites$.value.filter(item => item.sip !== contact.username)
      : [...this.favorites$.value, { sip: contact.username, name: contact.name }];
    this.save(favorites);
  }

  // імена колег могли змінитися в CRM — оновлюємо їх і у віджеті
  refreshNames(contacts: TransferTargetModel[]): void {
    let changed = false;
    const favorites = this.favorites$.value.map(item => {
      const contact = contacts.find(c => c.username === item.sip);
      if (!contact || contact.name === item.name) return item;
      changed = true;
      return { ...item, name: contact.name };
    });
    if (changed) this.save(favorites);
  }

  private static read(): QuickDialContact[] {
    try {
      const list = JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]');
      return Array.isArray(list) ? list.filter(item => item?.sip) : [];
    } catch {
      return [];
    }
  }

  private save(favorites: QuickDialContact[]): void {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
    this.favorites$.next(favorites);
    this.syncWidget();
  }

  private syncWidget(): void {
    if (!Capacitor.isPluginAvailable('QuickDial')) return; // старі збірки без віджета
    QuickDial.setFavorites({ contacts: this.favorites$.value }).catch(err => console.error('[QuickDial]', err));
  }

  private async handleUrl(url: string): Promise<void> {
    const match = url?.match(CALL_URL);
    if (!match) return;
    // подія і getLaunchUrl можуть принести те саме посилання
    if (this.lastUrl?.url === url && Date.now() - this.lastUrl.at < 5000) return;
    this.lastUrl = { url, at: Date.now() };

    const sip = match[1];
    const name = this.favorites$.value.find(item => item.sip === sip)?.name || sip;
    try {
      // застосунок щойно запустився: чекаємо входу і старту вбудованого телефону
      await firstValueFrom(this.store.select(GpAuthState.isAuthenticated).pipe(filter(Boolean), timeout(15000)));
      if (this.softphone.isEnabled) {
        const started = Date.now();
        while (!this.softphone.isActive && Date.now() - started < 5000) await new Promise(r => setTimeout(r, 200));
      }
    } catch {
      return; // не увійшли в застосунок
    }
    await this.internalCalls.call({ username: sip, name, isRegistered: true });
  }
}
